import React, { Component, useRef, useState, useEffect } from "react";
import { RouteComponentProps } from "react-router";
import {
  Spinner,
  Button,
  Divider,
  FormGroup,
  InputGroup,
  Tooltip,
  Toaster,
  Position
} from "@blueprintjs/core";
import Editor from "@monaco-editor/react";
import styled from "styled-components";
import { SaferEval } from "safer-eval";
import DesignPreview from "../DesignPreview";

const PreviewSidebar = styled.div`
  display: flex;
  flex-direction: column;
  background: hsl(240, 2%, 21%);
  border-left: 1px solid hsl(240, 2%, 29%);
  flex: 0 0 calc(256px + 2rem);
  padding: 1rem;
  overflow-y: auto;
  overflow-x: hidden;
`;

const CanvasPreview = styled.div`
  position: relative;
  padding-bottom: 100%;
  background: #000;
  border-radius: 3px;
  canvas {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    image-rendering: optimizeSpeed;
    image-rendering: -moz-crisp-edges;
    image-rendering: -webkit-optimize-contrast;
    image-rendering: -o-crisp-edges;
    image-rendering: crisp-edges;
    -ms-interpolation-mode: nearest-neighbor;
    image-rendering: -moz-crisp-edges;
    image-rendering: -webkit-crisp-edges
    image-rendering: pixelated;
  }
`;

const ThumbnailForm = styled.div`
  display: flex;
  align-items: center;
`;

const ThumbnailWrapper = styled.div`
  width: 50px;
  margin-right: 1rem;
`;

const initialCode = `// Write your code here
// - \`context\` is the canvas context
// - \`persistent\` is an empty object which is preserved between frames

if(persistent.hue === undefined) {
    persistent.hue = 180;
}

if(persistent.i === undefined) {
    persistent.i = 0;
}

context.fillStyle = "#000000";
context.fillRect(0, 0, 32, 32);

context.fillStyle = "hsl(" + persistent.hue + ", 100%, 50%)";
context.beginPath();
context.arc(16, 16, 16 * Math.abs(Math.cos(persistent.i)), 0, 2 * Math.PI);
context.fill();

persistent.i += 0.05;
persistent.hue = (persistent.hue + 1) % 360
`;

interface Props {
  document?: {
    title: string;
    data: string;
    user: string;
    code: string;
    approved: boolean;
  };
  disabled: boolean;
  pushToLive?: (data: string, type: string) => void;
  handleSave?: (script: string, data: string, title: string) => void;
  handleDelete?: () => void;
}

const ScriptEditor: React.SFC<Props> = ({
  document,
  disabled,
  handleSave,
  pushToLive,
  handleDelete
}) => {
  const [isEditorReady, setIsEditorReady] = useState(false);
  const valueGetter = useRef(null);
  const [code, setCode] = useState<string>(
    document !== null ? document.code : initialCode
  );
  const [name, setName] = useState<string>(
    document !== null ? document.title : ""
  );
  const [data, setData] = useState<string>(
    document !== null ? document.data : "0".repeat(6 * 32 * 32)
  );
  const persistentRef = React.useRef<object>({});

  const canvasRef = useRef(null);

  function handleEditorDidMount(_valueGetter) {
    setIsEditorReady(true);
    valueGetter.current = _valueGetter;
  }

  function handleShowValue() {
    persistentRef.current = {};
    setCode(valueGetter.current());
  }

  function rgb2hex(r, g, b) {
    let hex = x => ("0" + parseInt(x).toString(16)).slice(-2);
    return hex(r) + hex(g) + hex(b);
  }

  function setThumbnail() {
    let colorList = "";
    let canvas = canvasRef.current;
    let canvasContext = canvas.getContext("2d");
    var imageData = canvasContext.getImageData(0, 0, 32, 32);
    var data = imageData.data;
    for (var i = 0, n = data.length; i < n; i += 4) {
      var r = data[i];
      var g = data[i + 1];
      var b = data[i + 2];
      var hex = rgb2hex(r, g, b);
      colorList += hex;
    }
    setData(colorList);
  }

  function startSave() {
    persistentRef.current = {};
    const currentCode = valueGetter.current();
    setCode(currentCode);
    handleSave(currentCode, data, name);
  }

  function startPushToLive() {
    const currentCode = valueGetter.current();
    pushToLive(currentCode, "script");
  }

  useEffect(() => {
    let canvas = canvasRef.current;
    let canvasContext = canvas.getContext("2d");
    canvasContext.webkitImageSmoothingEnabled = false;
    canvasContext.mozImageSmoothingEnabled = false;
    canvasContext.imageSmoothingEnabled = false;
    let timeoutLoop;

    const context = {
      persistent: persistentRef.current,
      context: canvasContext
    };
    const safer = new SaferEval(context);

    const render = () => {
      try {
        safer.runInContext("(function(){" + code + "})()");
      } catch (error) {
        console.log(error);
      }
      timeoutLoop = setTimeout(render, 1000 / 30)
    };

    render();

    return () => {
      clearTimeout(timeoutLoop);
    };
  });

  return (
    <>
      <Editor
        theme="dark"
        height="calc(100vh - 50px)"
        language="javascript"
        // loading={<Spinner />}
        value={code}
        editorDidMount={handleEditorDidMount}
        options={{
          readOnly: disabled,
          // https://microsoft.github.io/monaco-editor/api/interfaces/monaco.editor.ieditoroptions.html
          minimap: {
            enabled: false
          }
        }}
      />
      <PreviewSidebar>
        <CanvasPreview>
          <canvas ref={canvasRef} width={32} height={32} />
        </CanvasPreview>
        {!disabled && (
          <React.Fragment>
            <br />
            <Button
              onClick={handleShowValue}
              disabled={!isEditorReady}
              intent="primary"
              large={true}
            >
              Update preview
            </Button>
            <br />
            <Divider />
          </React.Fragment>
        )}
        <br />
        <FormGroup label="Title">
          <InputGroup
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={disabled}
          />
        </FormGroup>
        {!disabled && (
          <FormGroup label="Thumbnail">
            <ThumbnailForm>
              <ThumbnailWrapper>
                <DesignPreview data={data} />
              </ThumbnailWrapper>
              <Button onClick={setThumbnail}>Set new thumbnail</Button>
            </ThumbnailForm>
          </FormGroup>
        )}
        {!disabled && (
          <Button
            onClick={startSave}
            disabled={!isEditorReady}
            intent="success"
            large={true}
          >
            Save script
          </Button>
        )}
        <br />
        {document !== null && document.approved && (
          <Button onClick={startPushToLive} disabled={!isEditorReady}>
            Push to live
          </Button>
        )}
        {document !== null && !document.approved && (
          <Tooltip content="Script has not yet been approved" targetTagName="div">
            <Button disabled style={{ width: "100%" }}>
              Push to live
            </Button>
          </Tooltip>
        )}
      </PreviewSidebar>
    </>
  );
};

export default ScriptEditor;
